import { type ReactNode } from "react";

import { BentoBoxPlaceholder } from "@react/sections/BentoBox/BentoBoxPlaceholder.view";
import { CollectionPlaceholder } from "@react/sections/Collection/CollectionPlaceholder.view";
import { ProductCollectionCarouselPlaceholder } from "@react/sections/ProductCollectionCarousel/ProductCollectionCarouselPlaceholder.view";
import { ProductHeroPlaceholder } from "@react/sections/ProductHero/ProductHeroPlaceholder.view";
import {
  type CollectionSectionSettings,
  type ProductHeroSettings,
} from "@root/tablescape/state";
import { type BentoBoxSectionSettings } from "@root/tablescape/state/bentoBox";

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null && !Array.isArray(value);
};

const getRecordValue = (value: unknown, key: string) => {
  if (!isRecord(value)) {
    return undefined;
  }

  return Reflect.get(value, key);
};

const getStringValue = (value: unknown, key: string) => {
  const recordValue = getRecordValue(value, key);
  return typeof recordValue === "string" ? recordValue : undefined;
};

const getProductCollectionCarouselPlaceholderData = (
  data?: Record<string, unknown>
) => {
  const tabs = getRecordValue(data, "tabs");
  const text = getRecordValue(data, "text");
  const tabLabels = Array.isArray(tabs)
    ? tabs
        .map((tab) => getStringValue(tab, "label"))
        .filter((label): label is string => Boolean(label))
    : [];

  return {
    tabLabels,
    viewAllText: getStringValue(text, "viewAll"),
  };
};

export const resolveZonePlaceholder = (
  componentName: string,
  data?: Record<string, unknown>
): ReactNode => {
  switch (componentName) {
    case "BentoBox": {
      const section = data as BentoBoxSectionSettings;
      return <BentoBoxPlaceholder {...section} />;
    }

    case "CollectionProvider": {
      const section = data as CollectionSectionSettings;

      const {
        collectionHero: { heroImage },
        pagination: { pageSize },
        settings: { version },
      } = section ?? {};

      if (version !== "v2") {
        return null;
      }

      return (
        <CollectionPlaceholder
          pageSize={pageSize}
          showHeroImage={!!heroImage}
        />
      );
    }

    case "ProductHeroRoot": {
      const section = data as ProductHeroSettings;

      const {
        addonUpsellProductsCount,
        crossProductBridgeOptionsCount,
        productOptionCategoriesCount,
        productSelectorExperience,
        showAddonUpsell,
        showCrossProductBridge,
        theme,
      } = section?.settings ?? {};

      const { lcpMedia } = section?.productGalleryCarousel ?? {};

      return (
        <ProductHeroPlaceholder
          addonUpsellProductsCount={addonUpsellProductsCount ?? 0}
          animateIn
          crossProductBridgeOptionsCount={crossProductBridgeOptionsCount}
          lcpMedia={lcpMedia}
          productOptionCategoriesCount={productOptionCategoriesCount}
          productSelectorExperience={productSelectorExperience}
          region="zone"
          showAddonUpsell={showAddonUpsell ?? false}
          showCrossProductBridge={showCrossProductBridge}
          theme={theme}
        />
      );
    }

    case "ProductCollectionCarousel": {
      const { tabLabels, viewAllText } =
        getProductCollectionCarouselPlaceholderData(data);

      return (
        <ProductCollectionCarouselPlaceholder
          activeIndex={0}
          includeHeader
          tabLabels={tabLabels}
          viewAllText={viewAllText}
        />
      );
    }

    default:
      return (
        <div className="placeholder animate-fade-in">
          <div className="m-1.5 box-border">
            <div className="box-border h-10 w-full animate-pulse rounded bg-placeholder md:h-14"></div>
          </div>
        </div>
      );
  }
};
